import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Compass, Download, LogOut, Search, Users, Loader2 } from "lucide-react";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/registrations");
      setRows(data);
    } catch {
      toast.error("Errore nel caricamento delle iscrizioni.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.nome, r.cognome, r.email, r.whatsapp, (r.attivita_sportiva || []).join(" ")].join(" ").toLowerCase().includes(q)
    );
  }, [rows, query]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/registrations/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "iscrizioni_osmand.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export CSV avviato.");
    } catch {
      toast.error("Errore durante l'export.");
    }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="dark min-h-screen bg-background text-foreground" data-testid="admin-dashboard">
      <header className="border-b border-border sticky top-0 bg-background/90 backdrop-blur z-20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-extrabold tracking-tight">
            <Compass className="w-5 h-5 text-primary" strokeWidth={2.5} /> OsmAnd · Dashboard
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-muted-foreground">{user?.email}</span>
            <Button onClick={doLogout} variant="outline" size="sm" className="rounded-full bg-transparent gap-2" data-testid="logout-btn">
              <LogOut className="w-4 h-4" /> Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Iscrizioni</h1>
            <div className="flex items-center gap-2 text-muted-foreground mt-3">
              <Users className="w-4 h-4" />
              <span className="tabular-nums" data-testid="total-count">{rows.length}</span> partecipanti registrati
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca…"
                className="pl-9 h-11 w-full md:w-64 rounded-lg"
              />
            </div>
            <Button onClick={exportCsv} className="rounded-full gap-2 bg-primary hover:bg-primary/90 h-11" data-testid="export-csv-btn">
              <Download className="w-4 h-4" /> Esporta CSV
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32 text-muted-foreground gap-3">
            <Loader2 className="w-5 h-5 animate-spin" /> Caricamento…
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-border rounded-2xl py-32 text-center text-muted-foreground" data-testid="empty-state">
            Nessuna iscrizione trovata.
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="border border-border rounded-2xl overflow-hidden overflow-x-auto"
          >
            <Table data-testid="registrations-table">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider">Nome</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">WhatsApp</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Email</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Sport</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Eventi</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Data</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id} className="border-border" data-testid={`row-${r.id}`}>
                    <TableCell className="font-semibold whitespace-nowrap">{r.nome} {r.cognome}</TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">{r.whatsapp}</TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">{r.email}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {(r.attivita_sportiva || []).map((s, i) => (
                          <span key={i} className="inline-block rounded-full bg-secondary text-secondary-foreground px-3 py-1 text-xs">{s}</span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <div className="flex flex-wrap gap-1">
                        {(r.eventi || []).filter(Boolean).map((ev, i) => (
                          <span key={i} className="text-xs text-muted-foreground border border-border rounded px-2 py-0.5">{ev}</span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground text-sm">
                      {new Date(r.created_at).toLocaleDateString("it-IT")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </motion.div>
        )}
      </main>
    </div>
  );
}
